import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock, CheckCircle2, MessageSquare, Loader2 } from 'lucide-react';
import { useChatHistory } from '../../hooks/connectivityHooks/useChatHistory'; // Adjust path

interface ChatHistoryViewProps {
  role: 'user' | 'expert';
  code: string;
}

export const ChatHistoryView: React.FC<ChatHistoryViewProps> = ({ role, code }) => {
  const navigate = useNavigate();
  const { sessions, isLoading } = useChatHistory(role, code);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-blue-500" />
        <p>Loading your conversation history...</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-slate-400 bg-white rounded-xl border border-slate-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-slate-300" />
        </div>
        <h3 className="text-lg font-bold text-slate-700 mb-1">No Messages Yet</h3>
        <p className="text-sm">When you complete consultations, your chat history will appear here.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[calc(100vh-120px)]">
      
      <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
        <h2 className="font-bold text-slate-800 text-lg flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-500" />
          Message History
        </h2>
        <div className="relative w-64 hidden sm:block">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input 
            type="text" 
            placeholder="Search messages..." 
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="divide-y divide-slate-100">
          {sessions.map((session) => (
            <div 
              key={session.id}
              onClick={() => navigate(`/chat/${session.expert_code}`, { 
                state: { 
                  sessionId: session.id,
                  expertName: role === 'expert' ? 'Me' : session.other_party_name,
                  isHistoryView: true 
                } 
              })}
              className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center justify-between group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg border border-blue-200 shrink-0">
                  {session.other_party_name[0].toUpperCase()}
                </div>
                
                <div>
                  <h3 className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                    {session.other_party_name}
                  </h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {session.category_name}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(session.started_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

           <div className="flex flex-col items-end gap-2">
  {session.status === 'completed' && (
    <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
      <CheckCircle2 className="w-3 h-3" />
      Completed
    </span>
  )}

  {session.status === 'active' && (
    <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
      <Clock className="w-3 h-3" />
      Active
    </span>
  )}

  {session.status === 'rejected' && (
    <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
      Rejected
    </span>
  )}

  {session.status === 'cancelled' && (
    <span className="flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
      Cancelled
    </span>
  )}

  {session.status === 'pending' && (
    <span className="flex items-center gap-1 text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
      Pending
    </span>
  )}

  {session.duration_seconds > 0 && (
    <span className="text-xs text-slate-400 font-mono">
      {Math.floor(session.duration_seconds / 60)}m {session.duration_seconds % 60}s
    </span>
  )}
</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};